import { useEffect, useState } from "react";
import { Loader2, Activity } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card, Pagination, StatusPill, fmtDateTime, EmptyState } from "./_shared";

const LIMIT = 50;
const STATUSES = ["", "running", "succeeded", "failed"];

export default function AdminJobs() {
  const [status, setStatus] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ items: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/jobs", {
          params: { status: status || undefined, skip, limit: LIMIT },
        });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, skip]);

  return (
    <AdminLayout
      title="Background jobs"
      subtitle="Async work — AI reruns, job searches, future LLM batch tasks. Read-only."
    >
      <div className="space-y-5">
        <Card className="p-4" testid="admin-jobs-counts">
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <Activity className="h-4 w-4 text-slate-400" />
            {["running", "succeeded", "failed"].map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1.5 text-slate-600"
              >
                <StatusPill
                  value={s}
                  tone={s === "running" ? "sky" : s === "succeeded" ? "green" : "rose"}
                />
                <span className="font-medium text-slate-800">
                  {data.counts?.[s] ?? 0}
                </span>
              </span>
            ))}
          </div>
        </Card>

        <Card className="p-4">
          <select
            value={status}
            onChange={(e) => {
              setSkip(0);
              setStatus(e.target.value);
            }}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
            data-testid="admin-jobs-status-select"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s ? `Status: ${s}` : "All statuses"}
              </option>
            ))}
          </select>
        </Card>

        <Card className="overflow-hidden">
          {loading ? (
            <div
              className="flex items-center gap-2 px-5 py-6 text-sm text-slate-500"
              data-testid="admin-jobs-loading"
            >
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : data.items.length === 0 ? (
            <EmptyState
              title="No background jobs yet"
              body="Reruns and async tasks will land here once they're executed."
              testid="admin-jobs-empty"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-2.5 font-medium">Job</th>
                    <th className="text-left px-5 py-2.5 font-medium">Kind</th>
                    <th className="text-left px-5 py-2.5 font-medium">Status</th>
                    <th className="text-left px-5 py-2.5 font-medium">Started</th>
                    <th className="text-left px-5 py-2.5 font-medium">Finished</th>
                    <th className="text-left px-5 py-2.5 font-medium">Target</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-testid="admin-jobs-tbody">
                  {data.items.map((j) => (
                    <tr key={j.job_id} className="hover:bg-slate-50">
                      <td className="px-5 py-3 font-mono text-xs">{j.job_id}</td>
                      <td className="px-5 py-3 text-slate-600">{j.kind}</td>
                      <td className="px-5 py-3">
                        <StatusPill
                          value={j.status}
                          tone={
                            j.status === "succeeded"
                              ? "green"
                              : j.status === "running"
                                ? "sky"
                                : j.status === "failed"
                                  ? "rose"
                                  : "slate"
                          }
                        />
                        {j.error && (
                          <div className="text-xs text-rose-600 mt-1 truncate max-w-md">
                            {j.error}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3 text-slate-500">{fmtDateTime(j.started_at)}</td>
                      <td className="px-5 py-3 text-slate-500">
                        {fmtDateTime(j.finished_at)}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-slate-500">
                        {j.target_id || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && data.items.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-100">
              <Pagination
                total={data.total}
                skip={skip}
                limit={LIMIT}
                onChange={setSkip}
                testidPrefix="admin-jobs"
              />
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}
